import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { classementsService, smasheursService, tournoisService } from '../services/api';

const TournoiDetail = () => {
  const { id } = useParams();
  const [tournoi, setTournoi] = useState(null);
  const [classements, setClassements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTournoiData = async () => {
    try {
      const [tournoisRes, classementsRes, smasheursRes] = await Promise.all([
        tournoisService.getAll(),
        classementsService.getAll(),
        smasheursService.getAll(),
      ]);

      // Trouver le tournoi par id
      const foundTournoi = tournoisRes.data.find(t => t.id === parseInt(id));
      setTournoi(foundTournoi);

      if (foundTournoi) {
        // Récupérer les classements : par startggId pour start.gg, par numSaucisse pour Challonge
        const tournoiClassements = foundTournoi.startggId
          ? classementsRes.data
              .filter(c => c.startggId === foundTournoi.startggId)
              .sort((a, b) => a.position - b.position)
          : classementsRes.data
              .filter(c => c.numSaucisse === foundTournoi.numSaucisse && !c.startggId)
              .sort((a, b) => a.position - b.position);

        // Enrichir avec les données des smasheurs
        const enrichedClassements = tournoiClassements.map(classement => {
          const smasheur = smasheursRes.data.find(s => s.idPlayerAPI === classement.smasheurId);
          return {
            ...classement,
            smasheur
          };
        });

        setClassements(enrichedClassements);
      }
    } catch (error) {
      console.error('Error loading tournoi data:', error);
    } finally {
      setLoading(false);
    }
    };

    loadTournoiData();
  }, [id]);

  const getTop8Image = (numSaucisse) => {
    try {
      return require(`../top8/saucismash-${numSaucisse}.jpg`);
    } catch {
      return null;
    }
  };

  const getMedalBadge = (position) => {
    if (position === 1) {
      return 'bg-warning text-dark';
    } else if (position === 2) {
      return 'bg-secondary';
    } else if (position === 3) {
      return 'bg-danger';
    }
    return 'bg-dark';
  };

  const getMedalIcon = (position) => {
    if (position === 1) return <i className="bi bi-trophy-fill me-1"></i>;
    if (position === 2) return <i className="bi bi-trophy me-1"></i>;
    if (position === 3) return <i className="bi bi-trophy me-1"></i>;
    return null;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (!tournoi) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Tournoi non trouvé
        </div>
        <Link to="/tournois" className="btn btn-primary">
          <i className="bi bi-arrow-left me-2"></i>
          Retour aux tournois
        </Link>
      </div>
    );
  }

  // Vérifier si c'est un tournoi Challonge
  const isChallongeTournoi = !!tournoi.challongeSlug;

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="mb-4">
        <Link to="/tournois" className="btn btn-outline-light mb-3">
          <i className="bi bi-arrow-left me-2"></i>
          Retour aux tournois
        </Link>

        <div className="hero-banner">
          <h1 className="hero-title">
            Saucismash #{tournoi.numSaucisse}
          </h1>
          <p className="mb-0">
            <i className="bi bi-people me-2"></i>
            {classements.length} participant{classements.length > 1 ? 's' : ''}
          </p>
          <div className="mt-3 d-flex gap-2 flex-wrap justify-content-center">
            {tournoi.slug && (
              <a
                href={`https://start.gg/${tournoi.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <i className="bi bi-link-45deg me-2"></i>
                Voir sur Start.gg
              </a>
            )}
            {tournoi.challongeSlug && (
              <a
                href={`https://challonge.com/fr/${tournoi.challongeSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-warning text-dark"
              >
                <i className="bi bi-link-45deg me-2"></i>
                Voir sur Challonge
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Podium pour tournois Challonge */}
      {isChallongeTournoi && classements.length >= 3 && (
        <div className="mb-5">
          <div className="row">
            <div className="col-12">
              <h3 className="text-center mb-4">
                <i className="bi bi-trophy me-2"></i>
                Podium
              </h3>
            </div>

            {/* 1ère place */}
            <div className="col-md-4 order-1 order-md-2 mb-3">
              <div className="card text-center h-100" style={{ transform: 'scale(1.1)' }}>
                <div className="card-body d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-trophy-fill" style={{ fontSize: '4rem', color: 'gold' }}></i>
                  </div>
                  <h3 className="mb-2">🏆 Vainqueur</h3>
                  {classements[0]?.smasheur && (
                    <Link to={`/smasheurs/${classements[0].smasheur.id}`} className="text-decoration-none">
                      <h4 className="text-warning">
                        {classements[0].smasheur.tag && `${classements[0].smasheur.tag} | `}
                        {classements[0].smasheur.pseudo}
                      </h4>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* 2ème place */}
            <div className="col-md-4 order-2 order-md-1 mb-3">
              <div className="card text-center h-100">
                <div className="card-body d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-trophy-fill" style={{ fontSize: '3rem', color: 'silver' }}></i>
                  </div>
                  <h4 className="mb-2">2ème place</h4>
                  {classements[1]?.smasheur && (
                    <Link to={`/smasheurs/${classements[1].smasheur.id}`} className="text-decoration-none">
                      <h5 className="text-white">
                        {classements[1].smasheur.tag && `${classements[1].smasheur.tag} | `}
                        {classements[1].smasheur.pseudo}
                      </h5>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* 3ème place */}
            <div className="col-md-4 order-3 order-md-3 mb-3">
              <div className="card text-center h-100">
                <div className="card-body d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-trophy-fill" style={{ fontSize: '3rem', color: '#CD7F32' }}></i>
                  </div>
                  <h4 className="mb-2">3ème place</h4>
                  {classements[2]?.smasheur && (
                    <Link to={`/smasheurs/${classements[2].smasheur.id}`} className="text-decoration-none">
                      <h5 className="text-white">
                        {classements[2].smasheur.tag && `${classements[2].smasheur.tag} | `}
                        {classements[2].smasheur.pseudo}
                      </h5>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Image top 8 */}
          {getTop8Image(tournoi.numSaucisse) && (
            <div className="text-center mt-4">
              <img
                src={getTop8Image(tournoi.numSaucisse)}
                alt={`Top 8 Saucismash #${tournoi.numSaucisse}`}
                className="img-fluid rounded"
                style={{ maxWidth: '800px', width: '100%' }}
              />
            </div>
          )}
        </div>
      )}

      {/* Podium pour tournois start.gg */}
      {!isChallongeTournoi && classements.length >= 3 && (
        <div className="mb-5">
          <div className="row">
            <div className="col-12">
              <h3 className="text-center mb-4">
                <i className="bi bi-trophy me-2"></i>
                Podium
              </h3>
            </div>

            {/* 1ère place */}
            <div className="col-md-4 order-1 order-md-2 mb-3">
              <div className="card text-center h-100" style={{ transform: 'scale(1.1)' }}>
                <div className="card-body d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-trophy-fill" style={{ fontSize: '4rem', color: 'gold' }}></i>
                  </div>
                  <h3 className="mb-2">🏆 Vainqueur</h3>
                  {classements[0]?.smasheur && (
                    <Link
                      to={`/smasheurs/${classements[0].smasheur.id}`}
                      className="text-decoration-none"
                    >
                      <h4 className="text-warning">
                        {classements[0].smasheur.tag && `${classements[0].smasheur.tag} | `}
                        {classements[0].smasheur.pseudo}
                      </h4>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* 2ème place */}
            <div className="col-md-4 order-2 order-md-1 mb-3">
              <div className="card text-center h-100">
                <div className="card-body d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-trophy-fill" style={{ fontSize: '3rem', color: 'silver' }}></i>
                  </div>
                  <h4 className="mb-2">2ème place</h4>
                  {classements[1]?.smasheur && (
                    <Link
                      to={`/smasheurs/${classements[1].smasheur.id}`}
                      className="text-decoration-none"
                    >
                      <h5 className="text-white">
                        {classements[1].smasheur.tag && `${classements[1].smasheur.tag} | `}
                        {classements[1].smasheur.pseudo}
                      </h5>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* 3ème place */}
            <div className="col-md-4 order-3 order-md-3 mb-3">
              <div className="card text-center h-100">
                <div className="card-body d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <i className="bi bi-trophy-fill" style={{ fontSize: '3rem', color: '#CD7F32' }}></i>
                  </div>
                  <h4 className="mb-2">3ème place</h4>
                  {classements[2]?.smasheur && (
                    <Link
                      to={`/smasheurs/${classements[2].smasheur.id}`}
                      className="text-decoration-none"
                    >
                      <h5 className="text-white">
                        {classements[2].smasheur.tag && `${classements[2].smasheur.tag} | `}
                        {classements[2].smasheur.pseudo}
                      </h5>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Image top 8 */}
          {getTop8Image(tournoi.numSaucisse) && (
            <div className="text-center mt-4">
              <img
                src={getTop8Image(tournoi.numSaucisse)}
                alt={`Top 8 Saucismash #${tournoi.numSaucisse}`}
                className="img-fluid rounded"
                style={{ maxWidth: '800px', width: '100%' }}
              />
            </div>
          )}
        </div>
      )}

      {/* Classement complet */}
      {classements.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="mb-0">
              <i className="bi bi-list-ol me-2"></i>
              Classement complet
            </h3>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Joueur</th>
                    <th>Tag</th>
                    <th>Personnages</th>
                  </tr>
                </thead>
                <tbody>
                  {classements.map((classement) => (
                    <tr key={classement.id}>
                    <td>
                      <span className={`badge ${getMedalBadge(classement.position)}`}>
                        {getMedalIcon(classement.position)}
                        {classement.position}
                      </span>
                    </td>
                    <td>
                      {classement.smasheur ? (
                        <Link
                          to={`/smasheurs/${classement.smasheur.id}`}
                          className="text-decoration-none fw-bold d-flex align-items-center"
                        >
                          {classement.smasheur.profileImage ? (
                            <img
                              src={classement.smasheur.profileImage}
                              alt={classement.smasheur.pseudo}
                              className="rounded-circle me-2"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                            />
                          ) : (
                            <i className="bi bi-person-circle me-2" style={{ fontSize: '1.5rem' }}></i>
                          )}
                          {classement.smasheur.pseudo}
                        </Link>
                      ) : (
                        <span className="text-muted">Inconnu</span>
                      )}
                    </td>
                    <td>
                      {classement.smasheur?.tag && (
                        <span className="badge bg-secondary">
                          {classement.smasheur.tag}
                        </span>
                      )}
                    </td>
                    <td className="text-muted small">
                      {classement.smasheur?.personnages || '-'}
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournoiDetail;
